import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { IndicatorType } from '@shared/types';
import {
  CandlestickUpdate,
  CandlestickUpdatePoint,
  createCandlestickUpdateStream,
  NewCandlestickEvent
} from './candlestick-update.service';
import {
  createIndicatorUpdateStream,
  IndicatorUpdate,
  IndicatorUpdateType
} from './indicator-update.service';
import {
  DataFeedResponse,
  TradingStats
} from '@server/data-feed/schemas/output.schema';
import {
  toCandlestickUpdatePoints,
  toLiquidationUpdatePoints,
  toMacdUpdatePoints,
  toMarketStructureUpdatePoints,
  toMasterPatternUpdatePoints,
  toMaUpdatePoints,
  toVwapUpdatePoints,
  toOrderBlocksUpdatePoints,
  toRsiUpdatePoints,
  toZigZagUpdatePoints
} from './feed-update.parsers';
import { mergeTimeframes, mergeTrades } from './feed-response-mergers';

export type ChartFeedUpdate = {
  updateType: IndicatorUpdateType;
  data: DataFeedResponse;
};

const mergeFeedResponse = (
  previous: DataFeedResponse | undefined,
  update: ChartFeedUpdate
): DataFeedResponse => {
  if (!previous || update.updateType === 'snapshot') {
    return update.data;
  }

  const updates = update.data;

  return {
    trades: mergeTrades(previous.trades || [], updates.trades || []),
    tradingStats: updates.tradingStats || previous.tradingStats,
    timeframes: mergeTimeframes(
      previous.timeframes || [],
      updates.timeframes || []
    )
  } as DataFeedResponse;
};
const emitCandlestickUpdates = (
  updates: DataFeedResponse,
  kind: IndicatorUpdateType,
  emitCandlestickUpdate: (update: CandlestickUpdate) => void
) => {
  if (!updates?.timeframes?.length) return;

  updates.timeframes.forEach((timeframe, timeframeIndex) => {
    const data = toCandlestickUpdatePoints(timeframe?.candlesticks || []);
    if (!data.length) return;

    emitCandlestickUpdate({
      timeframeIndex,
      updateType: kind,
      data
    });
  });
};

const emitIndicatorUpdates = (
  updates: DataFeedResponse,
  kind: IndicatorUpdateType,
  emitIndicatorUpdate: (update: IndicatorUpdate) => void
) => {
  if (!updates?.timeframes?.length) return;

  updates.timeframes.forEach((timeframe, timeframeIndex) => {
    const indicators = timeframe?.indicators;
    if (!indicators?.length) return;

    // Use per-type ordering to keep indicatorIndex stable across updates.
    const indicatorIndexByType = new Map<string, number>();
    indicators.forEach((indicator) => {
      const indicatorIndex = indicatorIndexByType.get(indicator.type) ?? 0;
      indicatorIndexByType.set(indicator.type, indicatorIndex + 1);

      switch (indicator.type) {
        case IndicatorType.MA: {
          const data = toMaUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        case IndicatorType.VWAP: {
          const data = toVwapUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        case IndicatorType.RSI: {
          const data = toRsiUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        case IndicatorType.MACD: {
          const data = toMacdUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        case IndicatorType.LIQUIDATION: {
          const data = toLiquidationUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        case IndicatorType.ZIG_ZAG: {
          const data = toZigZagUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        case IndicatorType.MASTER_PATTERN: {
          const data = toMasterPatternUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        case IndicatorType.ORDER_BLOCKS: {
          const data = toOrderBlocksUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        case IndicatorType.MARKET_STRUCTURE: {
          const data = toMarketStructureUpdatePoints(indicator.data || []);

          if (data.length) {
            emitIndicatorUpdate({
              timeframeIndex,
              indicatorIndex,
              indicatorType: indicator.type,
              updateType: kind,
              data
            });
          }
          return;
        }
        default:
          return;
      }
    });
  });
};

export type ChartUpdateStream = {
  emitFeedUpdate: (update: ChartFeedUpdate) => void;
  getFeedResponse$: () => Observable<DataFeedResponse | undefined>;
  getTradingStats$: () => Observable<TradingStats | undefined>;
  getLoading$: () => Observable<boolean>;
  setLoading: (value: boolean) => void;
  getCandlestickUpdate$: () => Observable<CandlestickUpdate>;
  getNewCandlestick$: () => Observable<NewCandlestickEvent>;
  getCandlestickData$: (
    timeframeIndex: number
  ) => Observable<CandlestickUpdatePoint[]>;
  getLatestCandlesticks: (timeframeIndex: number) => CandlestickUpdatePoint[];
  getCandlestickSnapshots: (timeframeIndex: number) => CandlestickUpdate[];
  getIndicatorUpdate$: () => Observable<IndicatorUpdate>;
  getIndicatorSnapshots: (
    timeframeIndex: number,
    indicatorType: IndicatorType
  ) => IndicatorUpdate[];
  reset: () => void;
  dispose: () => void;
};

export const createChartUpdateStream = (): ChartUpdateStream => {
  const candlestickStream = createCandlestickUpdateStream();
  const indicatorStream = createIndicatorUpdateStream();
  const feedResponse$ = new BehaviorSubject<DataFeedResponse | undefined>(
    undefined
  );
  const loading$ = new BehaviorSubject<boolean>(false);

  const emitFeedUpdate = (update: ChartFeedUpdate) => {
    const nextFeedResponse = mergeFeedResponse(
      feedResponse$.getValue(),
      update
    );
    feedResponse$.next(nextFeedResponse);
    emitCandlestickUpdates(
      update.data,
      update.updateType,
      candlestickStream.emitCandlestickUpdate
    );
    emitIndicatorUpdates(
      update.data,
      update.updateType,
      indicatorStream.emitIndicatorUpdate
    );
  };

  const getFeedResponse$ = () =>
    feedResponse$.asObservable().pipe(distinctUntilChanged());

  const getTradingStats$ = () =>
    feedResponse$.pipe(
      map((response) => response?.tradingStats),
      distinctUntilChanged()
    );

  const getLoading$ = () =>
    loading$.asObservable().pipe(distinctUntilChanged());

  const setLoading = (value: boolean) => {
    loading$.next(value);
  };

  const reset = () => {
    indicatorStream.clearIndicatorSnapshots();
    candlestickStream.resetCandlestickSnapshots();
    feedResponse$.next(undefined);
    loading$.next(false);
  };

  const dispose = () => {
    feedResponse$.complete();
    loading$.complete();
    indicatorStream.dispose();
    candlestickStream.dispose();
  };

  return {
    emitFeedUpdate,
    getFeedResponse$,
    getTradingStats$,
    getLoading$,
    setLoading,
    getCandlestickUpdate$: candlestickStream.getCandlestickUpdate$,
    getNewCandlestick$: candlestickStream.getNewCandlestick$,
    getCandlestickData$: candlestickStream.getCandlestickData$,
    getLatestCandlesticks: candlestickStream.getLatestCandlesticks,
    getCandlestickSnapshots: candlestickStream.getCandlestickSnapshots,
    getIndicatorUpdate$: indicatorStream.getIndicatorUpdate$,
    getIndicatorSnapshots: indicatorStream.getIndicatorSnapshots,
    reset,
    dispose
  };
};
